// pages/reports/AccountStatement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    ChevronUp,
    RotateCcw,
    //Download,
    //Printer,
    TrendingUp,
    TrendingDown,
    PieChart,
    //File,
    AlertCircle,
    Search,
    RefreshCw,
    ChevronDown,
    DollarSign,
    Calendar,
    Home,
    Briefcase,
    CreditCard,
    Circle,
    ArrowLeft,
    ArrowRight
} from 'feather-icons-react/build/IconComponents';
import { accountStatementActions } from '../../core/action/accountStatementActions';
import { bankActions } from '../../core/action/bankActions';

const MySwal = withReactContent(Swal);

const AccountStatement = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [statement, setStatement] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [transactionType, setTransactionType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 50; // Changed to constant since setter is not used
    const [expandedRows, setExpandedRows] = useState({});

    // Transaction type options
    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'credit', label: 'Credit Only' },
        { value: 'debit', label: 'Debit Only' }
    ];

    // Load accounts on mount
    useEffect(() => {
        loadAccounts();
    }, []);

    // Load accounts for dropdown
    const loadAccounts = async () => {
        try {
            const result = await bankActions.getAccounts({ limit: 100 });
            if (result.success) {
                const accountOptions = result.data.map(acc => ({
                    value: acc.id,
                    label: `${acc.bank_name} - ${acc.account_name} (${acc.account_number?.slice(-4) || 'XXXX'})`,
                    account: acc
                }));
                setAccounts(accountOptions);

                // Select first account by default if available
                if (accountOptions.length > 0 && !selectedAccount) {
                    setSelectedAccount(accountOptions[0]);
                }
            }
        } catch (error) {
            console.error('Error loading accounts:', error);
        }
    };

    // Format date for API (use local date parts to avoid UTC off-by-one)
    const formatDateForAPI = useCallback((date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Load statement data
    const loadStatement = useCallback(async (page = 0) => {
        if (!selectedAccount) {
            MySwal.fire({
                icon: 'warning',
                title: 'No Account Selected',
                text: 'Please select an account to view statement',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const params = {
                account_id: selectedAccount.value,
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                transaction_type: transactionType,
                search: searchTerm,
                limit: pageSize,
                offset: page * pageSize
            };

            const result = await accountStatementActions.getStatement(params);

            if (result.success) {
                setStatement(result.data);
                setCurrentPage(page);
            } else {
                setError(result.message || 'Failed to load statement');
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load statement',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading statement:', error);
            setError(error.message || 'Failed to load statement');
        } finally {
            setLoading(false);
        }
    }, [selectedAccount, fromDate, toDate, transactionType, searchTerm, pageSize, formatDateForAPI]);

    // Load when dependencies change
    useEffect(() => {
        if (selectedAccount) {
            loadStatement(0);
        }
    }, [loadStatement, selectedAccount]);

    // Handle account change
    const handleAccountChange = (option) => {
        setSelectedAccount(option);
        setCurrentPage(0);
        setExpandedRows({});
    };

    // Handle date changes
    const handleFromDateChange = (date) => {
        setFromDate(date);
    };

    const handleToDateChange = (date) => {
        setToDate(date);
    };

    // Handle transaction type change
    const handleTypeChange = (option) => {
        setTransactionType(option?.value || '');
        setCurrentPage(0);
        setExpandedRows({});
    };

    // Handle search
    const handleSearch = () => {
        setCurrentPage(0);
        setExpandedRows({});
        loadStatement(0);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(0);
        setExpandedRows({});
        setTimeout(() => loadStatement(0), 100);
    };

    // Handle refresh
    const handleRefresh = () => {
        loadStatement(currentPage);
    };

    // Handle next page
    const handleNextPage = () => {
        if (statement?.statement?.pagination?.has_more) {
            loadStatement(currentPage + 1);
        }
    };

    // Handle previous page
    const handlePrevPage = () => {
        if (currentPage > 0) {
            loadStatement(currentPage - 1);
        }
    };

    // Toggle row expansion
    const toggleRow = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Handle export PDF
    // const handleExportPDF = async () => {
    //     if (!selectedAccount) return;

    //     try {
    //         const params = {
    //             account_id: selectedAccount.value,
    //             from_date: formatDateForAPI(fromDate),
    //             to_date: formatDateForAPI(toDate),
    //             transaction_type: transactionType,
    //             search: searchTerm
    //         };

    //         const result = await accountStatementActions.exportToPDF(params);

    //         if (result) {
    //             const url = window.URL.createObjectURL(new Blob([result.data]));
    //             const link = document.createElement('a');
    //             link.href = url;
    //             link.setAttribute('download', `statement_${selectedAccount.label.replace(/[^a-z0-9]/gi, '_')}_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
    //             document.body.appendChild(link);
    //             link.click();
    //             link.remove();
    //         }
    //     } catch (error) {
    //         console.error('Error exporting PDF:', error);
    //         MySwal.fire({
    //             icon: 'error',
    //             title: 'Error',
    //             text: 'Failed to export PDF',
    //             timer: 2000,
    //             showConfirmButton: false
    //         });
    //     }
    // };

    // Handle export Excel
    // const handleExportExcel = async () => {
    //     if (!selectedAccount) return;

    //     try {
    //         const params = {
    //             account_id: selectedAccount.value,
    //             from_date: formatDateForAPI(fromDate),
    //             to_date: formatDateForAPI(toDate),
    //             transaction_type: transactionType,
    //             search: searchTerm
    //         };

    //         const result = await accountStatementActions.exportToExcel(params);

    //         if (result) {
    //             const url = window.URL.createObjectURL(new Blob([result.data]));
    //             const link = document.createElement('a');
    //             link.href = url;
    //             link.setAttribute('download', `statement_${selectedAccount.label.replace(/[^a-z0-9]/gi, '_')}_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
    //             document.body.appendChild(link);
    //             link.click();
    //             link.remove();
    //         }
    //     } catch (error) {
    //         console.error('Error exporting Excel:', error);
    //         MySwal.fire({
    //             icon: 'error',
    //             title: 'Error',
    //             text: 'Failed to export Excel',
    //             timer: 2000,
    //             showConfirmButton: false
    //         });
    //     }
    // };

    // Handle print
    // const handlePrint = () => {
    //     window.print();
    // };

    // Get account icon
    const getAccountIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'savings':
                return <Briefcase size={20} className="text-primary" />;
            case 'current':
                return <Home size={20} className="text-success" />;
            case 'cash':
                return <DollarSign size={20} className="text-info" />;
            case 'credit_card':
                return <CreditCard size={20} className="text-warning" />;
            default:
                return <Circle size={20} className="text-secondary" />;
        }
    };

    // Render account info card
    const renderAccountInfo = () => {
        if (!statement?.account) return null;

        const account = statement.account;

        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-8">
                            <div className="d-flex align-items-center mb-3">
                                <div className="avatar avatar-lg bg-light rounded d-flex align-items-center justify-content-center me-3">
                                    {getAccountIcon(account.account_type)}
                                </div>
                                <div>
                                    <h4 className="mb-1">{account.account_name}</h4>
                                    <p className="text-muted mb-0">
                                        {account.bank_name} | {account.account_number} | {account.ifsc_code || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4">
                                    <small className="text-muted d-block">Account Type</small>
                                    <strong>{accountStatementActions.formatTransactionType(account.account_type)}</strong>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-muted d-block">Branch</small>
                                    <strong>{account.branch_name || 'N/A'}</strong>
                                </div>
                                <div className="col-md-4">
                                    <small className="text-muted d-block">Status</small>
                                    <span className={`badge ${account.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                        {account.status?.toUpperCase()}
                                    </span>
                                    {account.is_default === 1 && (
                                        <span className="badge bg-primary ms-2">Default</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="bg-light p-3 rounded text-end">
                                <small className="text-muted d-block">Current Balance</small>
                                <h2 className={`mb-0 ${account.current_balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {accountStatementActions.formatCurrency(account.current_balance, account.currency)}
                                </h2>
                                <small className="text-muted">
                                    Opening: {accountStatementActions.formatCurrency(account.opening_balance, account.currency)}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render summary cards
    const renderSummaryCards = () => {
        if (!statement?.statement) return null;

        const { statement: stmt } = statement;

        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Opening Balance</p>
                                    <h3 className="mb-0 text-primary">
                                        {accountStatementActions.formatCurrency(stmt.opening_balance)}
                                    </h3>
                                    <small className="text-muted">As on {accountStatementActions.formatDate(stmt.from_date)}</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Calendar size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Credits</p>
                                    <h3 className="mb-0 text-success">
                                        {accountStatementActions.formatCurrency(stmt.total_credits)}
                                    </h3>
                                    <small className="text-muted">{stmt.transaction_count} Transactions</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Debits</p>
                                    <h3 className="mb-0 text-danger">
                                        {accountStatementActions.formatCurrency(stmt.total_debits)}
                                    </h3>
                                    <small className="text-muted">{stmt.transaction_count} Transactions</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Closing Balance</p>
                                    <h3 className="mb-0 text-info">
                                        {accountStatementActions.formatCurrency(stmt.closing_balance)}
                                    </h3>
                                    <small className="text-muted">
                                        Net Flow: {accountStatementActions.formatCurrency(stmt.net_flow)}
                                    </small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <PieChart size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">Select Account</label>
                            <Select
                                options={accounts}
                                value={selectedAccount}
                                onChange={handleAccountChange}
                                placeholder="Choose an account"
                                className="react-select"
                                classNamePrefix="select"
                                isClearable={false}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={handleFromDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={handleToDateChange}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Transaction Type</label>
                            <Select
                                options={typeOptions}
                                value={typeOptions.find(opt => opt.value === transactionType)}
                                onChange={handleTypeChange}
                                placeholder="All Types"
                                className="react-select"
                                classNamePrefix="select"
                                isClearable={true}
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={handleSearch}>
                                <Search size={16} className="me-2" />
                                Generate Statement
                            </button>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by description, reference, party, cheque no..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                {searchTerm && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={handleClearSearch}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <button className="btn btn-outline-secondary me-2" onClick={handleRefresh}>
                                <RefreshCw size={16} className="me-2" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render transactions table
    const renderTransactionsTable = () => {
        if (!statement?.statement?.transactions) return null;

        const transactions = statement.statement.transactions;

        if (transactions.length === 0) {
            return (
                <div className="card">
                    <div className="card-header">
                        <h5 className="mb-0">Transaction History</h5>
                    </div>
                    <div className="card-body">
                        <div className="alert alert-info">
                            No transactions found for the selected period.
                        </div>
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th className="text-end">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="table-primary">
                                        <td><strong>Opening Balance</strong></td>
                                        <td className="text-end">
                                            <strong>
                                                {accountStatementActions.formatCurrency(statement.statement.opening_balance)}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr className="table-primary">
                                        <td><strong>Closing Balance</strong></td>
                                        <td className="text-end">
                                            <strong>
                                                {accountStatementActions.formatCurrency(statement.statement.closing_balance)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Transaction History</h5>
                    <div>
                        <span className="text-muted me-3">
                            Showing {transactions.length} of {statement.statement.pagination.total} transactions
                        </span>
                    </div>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th>Reference/Cheque</th>
                                    <th>Party</th>
                                    <th className="text-end">Debit</th>
                                    <th className="text-end">Credit</th>
                                    <th className="text-end">Balance</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="table-primary">
                                    <td colSpan="8" className="text-end">
                                        <strong>Opening Balance</strong>
                                    </td>
                                    <td className="text-end">
                                        <strong>
                                            {accountStatementActions.formatCurrency(statement.statement.opening_balance)}
                                        </strong>
                                    </td>
                                    <td></td>
                                </tr>

                                {transactions.map((trans, idx) => (
                                    <React.Fragment key={trans.id || idx}>
                                        <tr>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => toggleRow(trans.id)}
                                                >
                                                    {expandedRows[trans.id] ?
                                                        <ChevronUp size={16} /> :
                                                        <ChevronDown size={16} />
                                                    }
                                                </button>
                                            </td>
                                            <td>{accountStatementActions.formatDate(trans.transaction_date)}</td>
                                            <td>
                                                <span className={`badge ${accountStatementActions.getTransactionTypeBadge(trans.transaction_type)}`}>
                                                    {accountStatementActions.formatTransactionType(trans.transaction_type)}
                                                </span>
                                            </td>
                                            <td>
                                                <div>{trans.description || 'No description'}</div>
                                                {trans.category && (
                                                    <small className="text-muted">{trans.category}</small>
                                                )}
                                            </td>
                                            <td>
                                                {trans.reference_no && (
                                                    <div><small>Ref: {trans.reference_no}</small></div>
                                                )}
                                                {trans.cheque_no && (
                                                    <div><small>Cheque: {trans.cheque_no}</small></div>
                                                )}
                                            </td>
                                            <td>{trans.party_name || '-'}</td>
                                            <td className="text-end text-danger">
                                                {trans.transaction_type === 'debit' ?
                                                    accountStatementActions.formatCurrency(trans.amount) : '-'
                                                }
                                            </td>
                                            <td className="text-end text-success">
                                                {trans.transaction_type === 'credit' ?
                                                    accountStatementActions.formatCurrency(trans.amount) : '-'
                                                }
                                            </td>
                                            <td className="text-end">
                                                <strong className={trans.running_balance >= 0 ? 'text-success' : 'text-danger'}>
                                                    {accountStatementActions.formatCurrency(trans.running_balance)}
                                                </strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${accountStatementActions.getStatusBadge(trans.status)}`}>
                                                    {trans.status?.toUpperCase() || 'CLEARED'}
                                                </span>
                                            </td>
                                        </tr>
                                        {expandedRows[trans.id] && (
                                            <tr className="table-light">
                                                <td colSpan="10">
                                                    <div className="p-3">
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <strong>Transaction Details</strong>
                                                                <p className="mb-1">ID: {trans.id}</p>
                                                                <p className="mb-1">Type: {trans.transaction_type}</p>
                                                                <p className="mb-1">Method: {trans.payment_method || 'N/A'}</p>
                                                                <p className="mb-1">Category: {trans.category || 'N/A'}</p>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <strong>Additional Info</strong>
                                                                <p className="mb-1">Created: {new Date(trans.created_at).toLocaleString()}</p>
                                                                {trans.linked_invoice_id && (
                                                                    <p className="mb-1">
                                                                        Linked Invoice: {trans.linked_invoice_type} #{trans.linked_invoice_id}
                                                                    </p>
                                                                )}
                                                                {trans.cheque_date && trans.cheque_date !== '0000-00-00' && (
                                                                    <p className="mb-1">Cheque Date: {accountStatementActions.formatDate(trans.cheque_date)}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}

                                <tr className="table-primary">
                                    <td colSpan="8" className="text-end">
                                        <strong>Closing Balance</strong>
                                    </td>
                                    <td className="text-end">
                                        <strong>
                                            {accountStatementActions.formatCurrency(statement.statement.closing_balance)}
                                        </strong>
                                    </td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {statement.statement.pagination.total > pageSize && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                <span className="text-muted">
                                    Page {currentPage + 1} of {Math.ceil(statement.statement.pagination.total / pageSize)}
                                </span>
                            </div>
                            <div>
                                <button
                                    className="btn btn-outline-primary me-2"
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 0}
                                >
                                    <ArrowLeft size={16} className="me-2" />
                                    Previous
                                </button>
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={handleNextPage}
                                    disabled={!statement.statement.pagination.has_more}
                                >
                                    Next
                                    <ArrowRight size={16} className="ms-2" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render category summary
    const renderCategorySummary = () => {
        if (!statement?.summary?.categories || statement.summary.categories.length === 0) return null;

        return (
            <div className="card mt-4">
                <div className="card-header">
                    <h5 className="mb-0">Category Summary</h5>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Category</th>
                                    <th className="text-end">Credits</th>
                                    <th className="text-end">Debits</th>
                                    <th className="text-end">Net</th>
                                    <th className="text-end">Transactions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statement.summary.categories.map((cat, idx) => {
                                    const net = (cat.total_credits || 0) - (cat.total_debits || 0);
                                    return (
                                        <tr key={idx}>
                                            <td>{cat.category || 'Uncategorized'}</td>
                                            <td className="text-end text-success">
                                                {accountStatementActions.formatCurrency(cat.total_credits || 0)}
                                            </td>
                                            <td className="text-end text-danger">
                                                {accountStatementActions.formatCurrency(cat.total_debits || 0)}
                                            </td>
                                            <td className={`text-end ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {accountStatementActions.formatCurrency(net)}
                                            </td>
                                            <td className="text-end">{cat.transaction_count || 0}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    if (loading && !statement) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Account Statement...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Account Statement</h4>
                            <h6>View detailed transaction history for your accounts</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">
                        {/* <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={handleExportPDF}
                                    disabled={!selectedAccount || !statement}
                                >
                                    <File size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={handleExportExcel}
                                    disabled={!selectedAccount || !statement}
                                >
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li> */}
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                >
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Filter Bar */}
                {renderFilterBar()}

                {/* Account Info */}
                {statement && renderAccountInfo()}

                {/* Summary Cards */}
                {statement && renderSummaryCards()}

                {/* Transactions Table */}
                {statement && renderTransactionsTable()}

                {/* Category Summary */}
                {statement && renderCategorySummary()}

                {/* Error Message */}
                {error && !loading && (
                    <div className="alert alert-danger">
                        <AlertCircle size={18} className="me-2" />
                        {error}
                    </div>
                )}

                {/* No Data Message */}
                {!statement && !error && selectedAccount && !loading && (
                    <div className="alert alert-info">
                        <AlertCircle size={18} className="me-2" />
                        No data available for the selected period. Try adjusting your filters.
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountStatement;